"use client";

import { createContext, useCallback, useContext, useState } from "react";

export type ToastType = "success" | "error" | "warning";

export interface Toast {
  id: number;
  type: ToastType;
  text: string;
}

interface ToastContextValue {
  /** @deprecated Prefer `toasts` — kept so older call sites still type-check. */
  toast: Toast | null;
  toasts: Toast[];
  showToast: (type: ToastType, text: string) => void;
  dismissToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue>({
  toast: null,
  toasts: [],
  showToast: () => {},
  dismissToast: () => {},
});

export function useToast() {
  return useContext(ToastContext);
}

let nextId = 1;

const DURATION_MS: Record<ToastType, number> = {
  success: 5000,
  warning: 12000,
  error: 16000,
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (type: ToastType, text: string) => {
      const id = nextId++;
      setToasts((prev) => {
        // Cap the stack so a bulk failure does not flood the screen.
        const next = [...prev, { id, type, text }];
        return next.slice(-6);
      });
      window.setTimeout(() => dismissToast(id), DURATION_MS[type]);
    },
    [dismissToast],
  );

  const toast = toasts.length ? toasts[toasts.length - 1] : null;

  return (
    <ToastContext.Provider value={{ toast, toasts, showToast, dismissToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function ToastOverlay() {
  const { toasts, dismissToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 left-0 right-0 z-50 flex flex-col items-center gap-2 px-4 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          className={`pointer-events-auto max-w-xl w-full sm:w-auto px-4 py-3 rounded-md text-sm font-medium shadow-lg flex items-start gap-3 ${
            t.type === "success"
              ? "bg-green-600 text-white"
              : t.type === "warning"
                ? "bg-amber-600 text-white"
                : "bg-red-600 text-white"
          }`}
        >
          <span className="flex-1 whitespace-pre-wrap break-words leading-snug">
            {t.text}
          </span>
          <button
            type="button"
            aria-label="Dismiss"
            className="shrink-0 opacity-80 hover:opacity-100 text-base leading-none"
            onClick={() => dismissToast(t.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
